import React, { useState, useEffect } from "react";
import "../../../css/AddCenterModal.css";
import api from "../../../api/axiosConfig";
import { showToast } from '../../Shared/Toaster';
import CitySelect from "../../Shared/CitySelect";


interface Props {
  isOpen: boolean;
  onClose: () => void;
  center: any; // existing center data
  onUpdate?: (center: any) => void;
}

const EditCenterModal: React.FC<Props> = ({ isOpen, onClose, center, onUpdate }) => {
  // Basic info
  const [name, setName] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [telephone1, setTelephone1] = useState("");
  const [telephone2, setTelephone2] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
//   const [status, setStatus] = useState("Active");

  // Working timings
  const [workingTimings, setWorkingTimings] = useState<any[]>([]);

  // Holidays
  const [holidays, setHolidays] = useState<any[]>([]);
  const [holidayName, setHolidayName] = useState("");
  const [holidayDate, setHolidayDate] = useState("");
  const [holidayDay, setHolidayDay] = useState("");

  const [loading, setLoading] = useState(false);

  // Pre-fill form when modal opens
  useEffect(() => {
    if (center) {
      setName(center.name || "");
      setAddressLine1(center.addressLine1 || "");
      setAddressLine2(center.addressLine2 || "");
      setCity(center.city || "");
      setTelephone1(center.telephone1 || "");
      setTelephone2(center.telephone2 || "");
      setLatitude(center.latitude?.toString() || "");
      setLongitude(center.longitude?.toString() || "");
    //   setStatus(center.status || "Active");

      // Working Timings
      const defaultTimings = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map(day => ({
        enabled: false,
        day,
        startTime: "09:00",
        endTime: "18:00",
      }));

      if (center.workingTimings?.length > 0) {
        setWorkingTimings(
          defaultTimings.map(t => {
            const match = center.workingTimings.find((wt: any) => wt.day === t.day);
            return match ? { ...t, enabled: true, startTime: match.startTime, endTime: match.endTime } : t;
          })
        );
      } else {
        setWorkingTimings(defaultTimings);
      }

      // Holidays
      setHolidays(center.holidays || []);
    }
  }, [center]);

  if (!isOpen) return null;

  // Update timing
  const handleTimingChange = (index: number, field: string, value: any) => {
    setWorkingTimings(prev =>
      prev.map((t, i) => (i === index ? { ...t, [field]: value } : t))
    );
  };

  // Handle holiday date → auto-fill day
  const handleHolidayDateChange = (dateValue: string) => {
    setHolidayDate(dateValue);
    if (dateValue) {
      const day = new Date(dateValue).toLocaleDateString("en-US", { weekday: "long" });
      setHolidayDay(day);
    } else {
      setHolidayDay("");
    }
  };

  // Add holiday
  const handleAddHoliday = () => {
    if (!holidayName.trim() || !holidayDate) {
      showToast("Please enter holiday name and date.", 'error');
      return;
    }
    setHolidays(prev => [...prev, { holidayName, day: holidayDay, date: holidayDate }]);
    setHolidayName("");
    setHolidayDate("");
    setHolidayDay("");
  };

  // ✅ Update handler
  const handleUpdate = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem("accessToken");

      const filteredWorkingTimings = workingTimings
        .filter(t => t.enabled)
        .map(t => ({ day: t.day, startTime: t.startTime, endTime: t.endTime }));

      const payload = {
        name,
        addressLine1,
        addressLine2,
        city,
        centerType:'QAVACH CENTER',
        telephone1,
        telephone2,
        latitude: latitude ? parseFloat(latitude) : undefined,
        longitude: longitude ? parseFloat(longitude) : undefined,
        // status,
        workingTimings: filteredWorkingTimings,
        holidays: holidays.map(h => ({
          holidayName: h.holidayName || h.name,
          day: h.day,
          date: new Date(h.date),
        })),
      };

      const res = await api.center_api.put(`/api/v1/centers/${center._id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      showToast("Center updated successfully ✅", 'success');
      if (onUpdate) onUpdate(res.data);
      onClose();
    } catch (err: any) {
      console.error("Error updating center:", err);
      showToast(err.response?.data?.message || "Could not update center ❌", 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-center-modal-overlay" onClick={onClose}>
      <div className="add-center-modal-container-add-center" onClick={(e) => e.stopPropagation()}>
        <h2>Edit Center</h2>

        {/* Basic Information */}
        <section className="add-center-form-section">
          <h3>Basic Information</h3>
          <div className="add-center-form-grid">
            <input value={name} onChange={e => setName(e.target.value)} />
            <input value={addressLine1} onChange={e => setAddressLine1(e.target.value)} />
            <input value={addressLine2} onChange={e => setAddressLine2(e.target.value)} />
            <CitySelect value={city} onChange={setCity} placeholder="Select city" />
            {/* <select value={status} onChange={e => setStatus(e.target.value)}>
              <option>Active</option>
              <option>Inactive</option>
            </select> */}
            <input value={telephone1} onChange={e => setTelephone1(e.target.value)} />
            <input value={telephone2} onChange={e => setTelephone2(e.target.value)} />
            <input value={latitude} onChange={e => setLatitude(e.target.value)} />
            <input value={longitude} onChange={e => setLongitude(e.target.value)} />
          </div>
        </section>

        {/* Working Timings */}
        <section className="add-center-form-section">
          <h3>Working Timings</h3>
          <table className="add-center-timings-table">
            <thead>
              <tr>
                <th>Enabled</th>
                <th>Day</th>
                <th>Start Time</th>
                <th>End Time</th>
              </tr>
            </thead>
            <tbody>
              {workingTimings.map((t, i) => (
                <tr key={i}>
                  <td>
                    <input
                      type="checkbox"
                      checked={t.enabled}
                      onChange={e => handleTimingChange(i, "enabled", e.target.checked)}
                    />
                  </td>
                  <td>{t.day}</td>
                  <td>
                    <input
                      type="time"
                      value={t.startTime}
                      onChange={e => handleTimingChange(i, "startTime", e.target.value)}
                      disabled={!t.enabled}
                    />
                  </td>
                  <td>
                    <input
                      type="time"
                      value={t.endTime}
                      onChange={e => handleTimingChange(i, "endTime", e.target.value)}
                      disabled={!t.enabled}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* Holiday Calendar */}
        <section className="add-center-form-section">
          <h3>Holiday Calendar</h3>
          <div className="add-center-holiday-inputs">
            <input placeholder="Enter holiday name" value={holidayName} onChange={e => setHolidayName(e.target.value)} />
            <input type="text" placeholder="Auto-filled" value={holidayDay} readOnly />
            <input type="date" value={holidayDate} onChange={e => handleHolidayDateChange(e.target.value)} />
            <button type="button" onClick={handleAddHoliday}>+ Add Holiday</button>
          </div>
          <table className="add-center-holiday-table">
            <thead>
              <tr>
                <th>Holiday Name</th>
                <th>Day</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {holidays.length === 0 ? (
                <tr><td colSpan={4} style={{ textAlign: "center" }}>No holidays added yet</td></tr>
              ) : (
                holidays.map((h, i) => (
                  <tr key={i}>
                    <td>{h.holidayName || h.name}</td>
                    <td>{h.day}</td>
                    <td>{new Date(h.date).toLocaleDateString("en-GB")}</td>
                    <td>
                      <button className="add-center-delete-btn" onClick={() => setHolidays(prev => prev.filter((_, idx) => idx !== i))}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#d32f2f" viewBox="0 0 24 24">
                      <path d="M3 6h18v2H3V6zm2 3h14l-1.5 12.5a2 2 0 0 1-2 1.5H8.5a2 2 0 0 1-2-1.5L5 9zm5 2v9h2v-9H10zm4 0v9h2v-9h-2zM9 4V2h6v2h5v2H4V4h5z" />
                    </svg>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </section>

        {/* Footer */}
        <div className="add-center-modal-footer">
          <button className="add-center-cancel-btn" onClick={onClose}>Cancel</button>
          <button className="add-center-submit-btn" onClick={handleUpdate}>
            {loading ? "Updating..." : "Update"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditCenterModal;
